'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import MenuCover from '@/app/_components/MenuCover';
import { useAppContext } from '@/app/_lib/_context';
import { SALE_CATEGORY_ID } from '@/app/_lib/config/navigation';

// Marketing page routes - "About" should be highlighted when on these pages
const MARKETING_ROUTES = ['/about-hive', '/contact-hive', '/hive-sales-assistance', '/hive-samples', '/hive-order-status', '/hive-modern-frequently-asked-questions', '/hive-modern-store-policy', '/hive-modern-privacy-policy', '/hive-modern-cookies-policy', '/catalog', '/newsletter-sign-up', '/hive-trade-program'];

// Hover intent timing (following Radix Navigation Menu pattern)
const DELAY_DURATION = 200; // ms to wait before opening (hover intent)
const CLOSE_DELAY = 150; // ms to wait before closing
const SKIP_DELAY_DURATION = 300; // ms window where switching is immediate

/**
 * MainMenu - Mega menu with Radix-style hover intent
 *
 * Three-timer approach:
 * 1. openTimerRef - delays opening to prevent accidental triggers (hover intent)
 * 2. closeTimerRef - delays closing to allow moving between trigger and dropdown
 * 3. skipDelayTimerRef - tracks window where switching between items is immediate
 *
 * Behavior:
 * - First hover on any item: wait DELAY_DURATION before opening
 * - Once menu is open, switching items is immediate (no delay)
 * - After closing, there's a SKIP_DELAY_DURATION window where reopening is immediate
 */

const MainMenu = t0 => {
  const $ = _c(63);
  const {
    menuItems,
    dropdownSlots: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const dropdownSlots = t2;
  const [openMenuId, setOpenMenuId] = useState(null);
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const {
    closeSearch,
    activeCategoryTree
  } = useAppContext();
  const pathname = usePathname();
  const navRef = useRef(null);
  let t3;
  if ($[2] !== pathname) {
    t3 = MARKETING_ROUTES.some(route => pathname === route || pathname?.startsWith(route + "/"));
    $[2] = pathname;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isMarketingPage = t3;
  const subMenuRef = useRef(null);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const mainMenuRefs = useRef(t4);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const skipDelayTimerRef = useRef(null);
  const isOpenDelayedRef = useRef(true);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      window.clearTimeout(openTimerRef.current);
      window.clearTimeout(closeTimerRef.current);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const clearTimers = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = nodeId => {
      clearTimers();
      setOpenMenuId(nodeId);
      isOpenDelayedRef.current = false;
      window.clearTimeout(skipDelayTimerRef.current);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const openMenu = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      clearTimers();
      setOpenMenuId(null);
      window.clearTimeout(skipDelayTimerRef.current);
      skipDelayTimerRef.current = window.setTimeout(() => {
        isOpenDelayedRef.current = true;
      }, SKIP_DELAY_DURATION);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const closeMenu = t7;
  let t8;
  if ($[8] !== closeSearch || $[9] !== openMenuId) {
    t8 = item => {
      closeSearch();
      window.clearTimeout(closeTimerRef.current);
      setHoveredItemId(item.node_id);
      const hasDropdown = item.children && item.children.length > 0 || item.saleCategories && item.saleCategories.length > 0;
      if (hasDropdown) {
        if (openMenuId === item.node_id) {
          return;
        }
        if (isOpenDelayedRef.current && openMenuId === null) {
          window.clearTimeout(openTimerRef.current);
          openTimerRef.current = window.setTimeout(() => {
            openMenu(item.node_id);
          }, DELAY_DURATION);
        } else {
          openMenu(item.node_id);
        }
      } else {
        window.clearTimeout(openTimerRef.current);
        closeMenu();
      }
    };
    $[8] = closeSearch;
    $[9] = openMenuId;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleItemEnter = t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      window.clearTimeout(openTimerRef.current);
      setHoveredItemId(null);
      closeTimerRef.current = window.setTimeout(() => {
        closeMenu();
      }, CLOSE_DELAY);
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleNavLeave = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      window.clearTimeout(closeTimerRef.current);
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const handleContentEnter = t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      closeMenu();
    };
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const handleLinkClick = t11;
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = event => {
      if (event.target.closest("a")) {
        closeMenu();
      }
    };
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const handleDropdownClick = t12;
  let t13;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => () => {
      window.clearTimeout(openTimerRef.current);
      window.clearTimeout(closeTimerRef.current);
      window.clearTimeout(skipDelayTimerRef.current);
    };
    t14 = [];
    $[15] = t13;
    $[16] = t14;
  } else {
    t13 = $[15];
    t14 = $[16];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[17] !== openMenuId) {
    t15 = () => {
      if (openMenuId !== null) {
        document.body.classList.add("submenu-open");
      } else {
        document.body.classList.remove("submenu-open");
      }
    };
    t16 = [openMenuId];
    $[17] = openMenuId;
    $[18] = t15;
    $[19] = t16;
  } else {
    t15 = $[18];
    t16 = $[19];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[20] !== openMenuId) {
    t17 = () => {
      const mediaQuery = window.matchMedia("(min-width: 1024px)");
      const handleChange = e => {
        if (!e.matches && openMenuId !== null) {
          closeMenu();
        }
      };
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    };
    t18 = [openMenuId, closeMenu];
    $[20] = openMenuId;
    $[21] = t17;
    $[22] = t18;
  } else {
    t17 = $[21];
    t18 = $[22];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      const handleClickOutside = event_0 => {
        if (navRef.current && !navRef.current.contains(event_0.target)) {
          closeMenu();
        }
      };
      window.addEventListener("mousedown", handleClickOutside);
      return () => window.removeEventListener("mousedown", handleClickOutside);
    };
    t20 = [closeMenu];
    $[23] = t19;
    $[24] = t20;
  } else {
    t19 = $[23];
    t20 = $[24];
  }
  useEffect(t19, t20);
  let t21;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => {
      if (!subMenuRef.current) {
        return [];
      }
      return Array.from(subMenuRef.current.querySelectorAll("[data-submenu-open=\"true\"] a[role=\"menuitem\"]"));
    };
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  const getSubmenuLinks = t21;
  let t22;
  let t23;
  if ($[26] !== menuItems || $[27] !== openMenuId) {
    t22 = () => {
      const handleKeyDown = event_1 => {
        const activeElement = document.activeElement;
        const isMainMenuItem = mainMenuRefs.current.includes(activeElement);
        const submenuLinks = getSubmenuLinks();
        const isInSubmenu = submenuLinks.includes(activeElement);
        const currentSubmenuIndex = submenuLinks.indexOf(activeElement);
        if (event_1.key === "Escape" && openMenuId !== null) {
          event_1.preventDefault();
          const mainIndex = menuItems.findIndex(item_0 => item_0.node_id === openMenuId);
          closeMenu();
          mainMenuRefs.current[mainIndex]?.focus();
          return;
        }
        if (isInSubmenu) {
          switch (event_1.key) {
            case "ArrowDown":
              {
                event_1.preventDefault();
                if (currentSubmenuIndex < submenuLinks.length - 1) {
                  submenuLinks[currentSubmenuIndex + 1].focus();
                } else {
                  submenuLinks[0].focus();
                }
                return;
              }
            case "ArrowUp":
              {
                event_1.preventDefault();
                if (currentSubmenuIndex > 0) {
                  submenuLinks[currentSubmenuIndex - 1].focus();
                } else {
                  const mainIndex_0 = menuItems.findIndex(item_1 => item_1.node_id === openMenuId);
                  mainMenuRefs.current[mainIndex_0]?.focus();
                }
                return;
              }
            case "Home":
              {
                event_1.preventDefault();
                submenuLinks[0]?.focus();
                return;
              }
            case "End":
              {
                event_1.preventDefault();
                submenuLinks[submenuLinks.length - 1]?.focus();
                return;
              }
          }
        }
        if (!isMainMenuItem) {
          return;
        }
        bb151: switch (event_1.key) {
          case "ArrowRight":
            {
              event_1.preventDefault();
              const nextIndex = (mainMenuRefs.current.indexOf(activeElement) + 1) % menuItems.length;
              mainMenuRefs.current[nextIndex]?.focus();
              break bb151;
            }
          case "ArrowLeft":
            {
              event_1.preventDefault();
              const currentIdx = mainMenuRefs.current.indexOf(activeElement);
              const prevIndex = currentIdx === 0 ? menuItems.length - 1 : currentIdx - 1;
              mainMenuRefs.current[prevIndex]?.focus();
              break bb151;
            }
          case "ArrowDown":
            {
              event_1.preventDefault();
              if (openMenuId !== null && submenuLinks.length > 0) {
                submenuLinks[0].focus();
              } else {
                const focusedIdx = mainMenuRefs.current.indexOf(activeElement);
                const menuItem_0 = menuItems[focusedIdx];
                if (menuItem_0?.children && menuItem_0.children.length > 0) {
                  openMenu(menuItem_0.node_id);
                  setTimeout(() => {
                    const links = getSubmenuLinks();
                    links[0]?.focus();
                  }, 50);
                }
              }
              break bb151;
            }
          case "Home":
            {
              event_1.preventDefault();
              mainMenuRefs.current[0]?.focus();
              break bb151;
            }
          case "End":
            {
              event_1.preventDefault();
              mainMenuRefs.current[menuItems.length - 1]?.focus();
              break bb151;
            }
          case "Enter":
          case " ":
            {
              const focusedIndex = mainMenuRefs.current.indexOf(activeElement);
              const menuItem = menuItems[focusedIndex];
              if (menuItem?.children && menuItem.children.length > 0) {
                event_1.preventDefault();
                if (openMenuId === menuItem.node_id) {
                  closeMenu();
                } else {
                  openMenu(menuItem.node_id);
                }
              }
            }
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t23 = [menuItems, openMenuId, openMenu, closeMenu, getSubmenuLinks];
    $[26] = menuItems;
    $[27] = openMenuId;
    $[28] = t22;
    $[29] = t23;
  } else {
    t22 = $[28];
    t23 = $[29];
  }
  useEffect(t22, t23);
  let t24;
  if ($[30] !== menuItems || $[31] !== openMenuId) {
    let t25;
    if ($[33] !== openMenuId) {
      t25 = item_2 => item_2.node_id === openMenuId;
      $[33] = openMenuId;
      $[34] = t25;
    } else {
      t25 = $[34];
    }
    t24 = menuItems.find(t25);
    $[30] = menuItems;
    $[31] = openMenuId;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  const openItem = t24;
  const t25 = openMenuId !== null;
  let t26;
  if ($[35] !== t25) {
    t26 = <MenuCover coverVisible={t25} close={closeMenu} />;
    $[35] = t25;
    $[36] = t26;
  } else {
    t26 = $[36];
  }
  let t27;
  if ($[37] !== activeCategoryTree || $[38] !== handleItemEnter || $[39] !== hoveredItemId || $[40] !== isMarketingPage || $[41] !== menuItems || $[42] !== openMenuId) {
    t27 = menuItems.map((item_3, index) => {
      const hasChildren = item_3.children && item_3.children.length > 0 || item_3.saleCategories && item_3.saleCategories.length > 0;
      const isOpen = openMenuId === item_3.node_id;
      const isAboutItem = item_3.url?.includes("/about-hive");
      const isActive = item_3.category_id > 0 && activeCategoryTree.includes(item_3.category_id) || isAboutItem && isMarketingPage;
      return <li key={item_3.node_id} role="none" className="px-3 lg:max-xl:px-2.5 first:pl-0 last:pr-0 text-left" onMouseEnter={() => handleItemEnter(item_3)}><Link ref={el => mainMenuRefs.current[index] = el} href={item_3.url || "#"} onClick={handleLinkClick} role="menuitem" aria-haspopup={hasChildren ? "true" : undefined} aria-expanded={hasChildren ? isOpen : undefined} className={`relative py-2.5 block text-md lg:max-xl:text-base font-light capitalize text-nowrap hover:text-primary ${item_3.category_id === SALE_CATEGORY_ID ? "text-red" : ""} ${isActive ? "text-primary" : ""} ${hoveredItemId === item_3.node_id && item_3.category_id !== SALE_CATEGORY_ID ? "text-primary" : ""} ${isOpen ? "text-primary after:content-[\"\"] after:block after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-primary after:translate-y-[1px] after:z-50" : ""}`}>{item_3.name}</Link></li>;
    });
    $[37] = activeCategoryTree;
    $[38] = handleItemEnter;
    $[39] = hoveredItemId;
    $[40] = isMarketingPage;
    $[41] = menuItems;
    $[42] = openMenuId;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== t27) {
    t28 = <ul role="menubar" className="inline-flex bg-transparent items-center justify-center content-end list-none">{t27}</ul>;
    $[44] = t27;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  const t29 = openItem ? `${openItem.name} submenu` : "Submenu";
  const t30 = `relative z-20 lg:z-30 w-full lg:absolute lg:left-0 lg:right-0 lg:top-full lg:min-h-[250px] lg:border-t lg:border-gray-8 my-0 lg:px-[35px] lg:pt-7 text-left bg-white transition-all duration-400 ease-out ${openMenuId !== null ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4 pointer-events-none"}`;
  let t31;
  if ($[46] !== dropdownSlots || $[47] !== menuItems || $[48] !== openMenuId) {
    let t32;
    if ($[50] !== dropdownSlots || $[51] !== openMenuId) {
      t32 = (item_4, index_0) => {
        const isOpen_0 = openMenuId === item_4.node_id;
        return <div key={item_4.node_id} data-submenu-open={isOpen_0 ? "true" : "false"} className={isOpen_0 ? "block animate-fadeIn" : "hidden"}>{dropdownSlots[index_0]}</div>;
      };
      $[50] = dropdownSlots;
      $[51] = openMenuId;
      $[52] = t32;
    } else {
      t32 = $[52];
    }
    t31 = menuItems.map(t32);
    $[46] = dropdownSlots;
    $[47] = menuItems;
    $[48] = openMenuId;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[53] !== t29 || $[54] !== t30 || $[55] !== t31) {
    t32 = <div ref={subMenuRef} role="menu" aria-label={t29} onMouseEnter={handleContentEnter} onClick={handleDropdownClick} className={t30}>{t31}</div>;
    $[53] = t29;
    $[54] = t30;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  let t33;
  if ($[57] !== t28 || $[58] !== t32) {
    t33 = <nav ref={navRef} onMouseLeave={handleNavLeave} className="px-0 delay-200 duration-200 transition-all" aria-label="Main navigation">{t28}{t32}</nav>;
    $[57] = t28;
    $[58] = t32;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== t26 || $[61] !== t33) {
    t34 = <div className="desktop-nav">{t26}{t33}</div>;
    $[60] = t26;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  return t34;
};
export default MainMenu;